import { styled } from "@mui/system";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { trackEvent } from "src/Amplitude";
import PriceAlertModel from "src/components/markets/PriceAlerts/PriceAlertModel";
import BellIcon from "src/icons/portfolio/BellIcon";
import RedStarIcon from "src/icons/portfolio/RedStar";
import StarIcon from "src/icons/portfolio/StarIcon";
import { togglePriceAlertModal } from "src/store/features/PriceAlerts.slice";
import { setDepositModal } from "src/store/features/modals.slice";
import { useAppDispatch } from "src/store/store";
import { useUserSlice } from "src/store/features/user.slice";
import { IWatchlistData } from "../defaults";
import useDeviceSizes from "../hooks/useDeviceSizes";
import WatchlistSparkline from "./WatchlistSparkline";

export const TableContainer = styled("div")(({ theme }) => ({
  display: "flex",
  height: "260px",
  width: "100%",
  overflowY: "auto",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "22px",
  paddingRight: "10px", // give padding to scrollbar

  [theme.breakpoints.down(720)]: {
    gap: "24px",
    alignSelf: "stretch",
  },

  /* Style scrollbar for WebKit (Chrome, Safari) */
  "&::-webkit-scrollbar": {
    width: "5px" /* Width of the scrollbar */,
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#E5E7E8" /* Color of the scrollbar thumb */,
    borderRadius: "5px" /* Border radius of the scrollbar thumb */,
  },

  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent" /* Color of the scrollbar thumb */,
  },
}));

export const Table = styled("table")({
  width: "100%",
  padding: 0,
  borderCollapse: "separate",
  borderSpacing: "0px 16px",
  fontSize: "14px",
});

export const TableHead = styled("thead")({
  textAlign: "left",
  fontSize: "12px",
  top: 0,
  background: "#FFF",
  zIndex: 999,
  position: "sticky",
  "@supports (position: -webkit-sticky)": {
    position: "-webkit-sticky",
  },
});

export const TableHeadCell = styled("th")(({ theme }) => ({
  fontWeight: 400,
  color: "#002035",
  marginBottom: "12px",

  [theme.breakpoints.down(720)]: {
    paddingTop: "15px",
    paddingBottom: "5px",
  },
}));

interface TableBodyCellProps {
  width?: string;
  sparklineCell?: boolean;
}

export const TableBodyCell = styled("td")<TableBodyCellProps>(({ width }) => ({
  width: width,
  whiteSpace: "nowrap",
  fontSize: "14px",
}));

interface TableIconProps {
  // i RouterLink does not like custom boolean values and will cause an error in console but it will accept string ones
  ishoverable: string;
}
export const TableIcon = styled(RouterLink)<TableIconProps>(({ ishoverable }) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  fontWeight: 400,
  textDecoration: "none",
  color: "inherit" /* Use the default text color */,
  cursor: ishoverable === "true" ? "pointer" : "default",

  "&:hover": {
    color: ishoverable === "true" ? "#4E7086" : "",
  },
}));

export const TableChart = styled("td")({
  height: "25px",
});

export const TableDepositButton = styled(RouterLink)({
  width: "65px",
  display: "flex",
  padding: "4px 8px",
  alignItems: "center",
  justifyContent: "center",
  height: "27px",
  gap: "4px",
  borderRadius: "16px",
  border: "1px solid #002035",
  background: "#FFF",
  alignSelf: "stretch",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit",

  "&:hover": {
    background: "#F8F9F9",
  },

  "&:focus": {
    backgroundColor: "#002035",
    color: "#FFFFFF",
  },
});

export const TableTradeButton = styled(RouterLink)({
  width: "52px",
  display: "flex",
  padding: "4px 8px",
  alignItems: "center",
  justifyContent: "center",
  height: "27px",
  gap: "4px",
  borderRadius: "16px",
  border: "1px solid #002035",
  background: "#FFF",
  alignSelf: "stretch",
  cursor: "pointer",
  textDecoration: "none",
  color: "inherit",

  "&:hover": {
    background: "#F8F9F9",
  },

  "&:focus": {
    backgroundColor: "#002035",
    color: "#FFFFFF",
  },
});

export const TableFavourite = styled("div")({
  cursor: "pointer",
});

export const TableAlert = styled("div")({
  cursor: "pointer",
});

interface PortfolioTableProps {
  data: IWatchlistData[];
  selectedfilter: string;
  onFavouriteClick: (tokenToAdd: IWatchlistData) => void;
}

const PortfolioTable = (props: PortfolioTableProps) => {
  const { data, selectedfilter, onFavouriteClick } = props;
  const { isMobile } = useDeviceSizes();
  const dispatch = useAppDispatch();
  // Read watchlist directly from Redux for instant updates
  const { watchlist } = useUserSlice();
  const [selected, setSelected] = React.useState({
    price: 0,
    currencyConfig: { formatName: "", type: "", name: "", code: "", networkName: "" },
    subtitle: "",
    logoUrl: "",
  });

  // Helper function to check if coin is in watchlist (reads directly from Redux)
  // Normalize both codes to uppercase for comparison since coin.code is lowercase but watchlist codes are uppercase
  const isCoinInWatchlist = (coinCode: string): boolean => {
    if (!coinCode || !watchlist) return false;
    const normalizedCoinCode = coinCode.toUpperCase();
    return watchlist.some(
      (item) => item.currencyConfig?.code?.toUpperCase() === normalizedCoinCode,
    );
  };

  const handleSelectedPriceAlert = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    coin: IWatchlistData,
  ) => {
    setSelected({
      price: coin.rawPrice,
      currencyConfig: coin.currencyConfig,
      subtitle: coin.currencyConfig.formatName,
      logoUrl: coin.currencyConfig.logoUrl,
    });
    dispatch(togglePriceAlertModal());
  };

  const handleNagivateToDeposit = () => {
    dispatch(setDepositModal(true));
  };

  const [isHovered, setIsHovered] = React.useState<Record<string, number | boolean>>({
    id: 1,
    favorite: false,
    alert: false,
  });

  const handleMouseEnter = (index: number, favorite: boolean, alert: boolean) => {
    setIsHovered((prev) => {
      return { ...prev, id: index, favorite, alert };
    });
  };

  const handleMouseLeave = (index: number, favorite: boolean, alert: boolean) => {
    setIsHovered((prev) => {
      return { ...prev, id: index, favorite, alert };
    });
  };

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <tr style={{ position: "sticky" }}>
              <TableHeadCell>Coin</TableHeadCell>
              {/* only on desktop & tablet */}
              {<TableHeadCell>Price</TableHeadCell>}
              {/* only on desktop & tablet */}
              {!isMobile && <TableHeadCell>Value</TableHeadCell>}
              <TableHeadCell>24h</TableHeadCell>
            </tr>
          </TableHead>
          <tbody id={`${selectedfilter}-table-body`}>
            {data.map((coin, index) => {
              const ampTrack = (button: string) => {
                trackEvent(
                  `Button_Click`,
                  {
                    button: `${button}`,
                  },
                  "DesktopTablet",
                );
              };
              return (
                <tr key={coin.name}>
                  {/* NAME AND ICON */}
                  <TableBodyCell width="15%" onClick={() => ampTrack(coin.code)}>
                    <TableIcon
                      ishoverable={coin.currencyConfig.type === "Crypto" ? "true" : "false"}
                      to={coin.currencyConfig.type === "Fiat" ? "#" : `/coin/${coin.code}`}>
                      {typeof coin.icon === "string" ? (
                        <img src={coin.icon} alt={coin.name} height="24px" width="24px" />
                      ) : (
                        coin.icon
                      )}
                      <div>{coin.name} </div>
                    </TableIcon>
                  </TableBodyCell>

                  {/* Price - only on desktop & tablet */}
                  {
                    <TableBodyCell width="12%" translate="no">
                      {coin.price}
                    </TableBodyCell>
                  }

                  {/* Market cap - only on desktop & tablet*/}
                  {!isMobile && (
                    <TableBodyCell width="11.11%" translate="no">
                      {coin.marketCap}
                    </TableBodyCell>
                  )}

                  {/* 24h change */}
                  <TableBodyCell width="8%">
                    <span
                      style={{
                        padding: "2px 6px",
                        borderRadius: "8px",
                        fontWeight: "500",
                        display: "inline-block",
                        minWidth: "60px",
                        textAlign: "center",
                      }}
                      translate="no">
                      {coin.change}
                    </span>
                  </TableBodyCell>
                  {/* Spark line area chart */}
                  {!isMobile && (
                    <TableBodyCell width="20%" sparklineCell>
                      <WatchlistSparkline data={coin.sparkline} />
                    </TableBodyCell>
                  )}

                  {/* DEPOSIT BUTTON */}
                  {!isMobile && (
                    <TableBodyCell
                      width="8%"
                      style={{ paddingRight: "15px" }}
                      onClick={() => ampTrack("Deposit")}>
                      <TableDepositButton
                        onClick={handleNagivateToDeposit}
                        to={`/wallets/deposit?code=${coin.code}`}>
                        Deposit
                      </TableDepositButton>
                    </TableBodyCell>
                  )}

                  {/* TRADE BUTTON - only on desktop & tablet */}
                  {!isMobile && (
                    <TableBodyCell
                      width="10%"
                      style={{ paddingLeft: "15px" }}
                      onClick={() => ampTrack("Trade")}>
                      <TableTradeButton to={`/trade?code=${coin.code}&type=buy`}>
                        Trade
                      </TableTradeButton>
                    </TableBodyCell>
                  )}

                  {/* FAVOURITE BUTTON - only on desktop & tablet */}
                  {!isMobile && (
                    <TableBodyCell width="1%" onClick={() => ampTrack("Favourite")}>
                      <TableFavourite
                        id={`favourite-${coin.code}`}
                        style={{ textAlign: "right" }}
                        onMouseEnter={() => handleMouseEnter(index, true, false)}
                        onMouseLeave={() => handleMouseLeave(index, false, false)}
                        onClick={(): void => onFavouriteClick(coin)}>
                        {isCoinInWatchlist(coin.code) ||
                        (isHovered.favorite && isHovered.id === index) ? (
                          <RedStarIcon />
                        ) : (
                          <StarIcon />
                        )}
                      </TableFavourite>
                    </TableBodyCell>
                  )}

                  {/* PRICE ALERT BUTTON - only on desktop & tablet*/}
                  {!isMobile && (
                    <TableBodyCell width="5%" onClick={() => ampTrack("Alert")}>
                      <TableAlert
                        style={{ textAlign: "right" }}
                        onMouseEnter={() => handleMouseEnter(index, false, true)}
                        onMouseLeave={() => handleMouseLeave(index, false, false)}
                        onClick={(e) => handleSelectedPriceAlert(e, coin)}>
                        {isHovered.alert && isHovered.id === index ? (
                          <BellIcon color="#4E7086" />
                        ) : (
                          <BellIcon />
                        )}
                      </TableAlert>
                    </TableBodyCell>
                  )}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </TableContainer>

      <PriceAlertModel
        shouldRedirect={false}
        price={selected.price}
        currencyConfig={selected.currencyConfig}
        subtitle={selected.subtitle}
        logoUrl={selected.logoUrl}
      />
    </>
  );
};
export default PortfolioTable;
